// Which analytics positions a wizard write EDITED (ONBOARDING_EDIT_TRACKING spec §3).
//
// `onboarding_substep_saved` says a screen was finished; it is equally true of the school
// that typed every field and the one that accepted every prefill and pressed Next. The
// `onboarding_substep_edited` event is the second witness: THE USER CHANGED THIS SCREEN'S
// DATA. This module decides which positions a single `updateData` write counts as editing.
//
// Rules, in the order they matter:
//  1. Only writes without `options.prefill` reach here — the caller already filters. That
//     flag is the whole prefill/edit distinction; nothing here second-guesses it.
//  2. A write that changes nothing is not an edit. Step components spread the whole
//     `manualData` into every call, so every field is compared by value.
//  3. A position is only attributed while the user is ON the main step that completes it.
//     Editing Branches on Institution Setup re-folds the Academic Structure's class rows on
//     that page's next commit; that is not an Academic Structure edit.
//  4. When in doubt, do not mark. An over-reported edit is a wrong answer; an under-reported
//     one is a missing answer, and the payload keeps missing answers visible.
import {SETUP_FIELDS_BY_POSITION, stepCompletingPosition} from '../onboardingSections'
import {subjectLevelKey} from './academicStructure'

type Loose = Record<string, any>

/** The mode Setup Basics pre-selects (Step1Mode's prefill; class-wise carries "Recommended"). */
export const DEFAULT_WIZARD_MODE = 'class' as const

const same = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/**
 * null / undefined / [] / {} are all "nothing here". Step 4 materialises its empty buckets on
 * mount (`lessonPlanningData ?? {}`), and that must not read as the user filling them in.
 */
const isBlank = (value: unknown): boolean =>
  value == null ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && Object.keys(value as object).length === 0)

/** True when `after` carries `field` with a value that differs from `before`'s. */
const changed = (before: Loose, after: Loose, field: string): boolean => {
  if (!(field in after)) return false
  if (isBlank(before[field]) && isBlank(after[field])) return false
  return !same(before[field], after[field])
}

// --- Academic Structure projections ----------------------------------------------------
//
// The page edits one nested structure (levels > classes > sections, subjects per level) and
// `applyAcademicStructure` flattens it into a dozen derived fields — renaming a level rewrites
// `classes`, `subjects` and the review rows too. Diffing those flattened fields would credit
// every position for any edit, so each position is diffed on its OWN projection of the data.
//
// The class/section/subject projections are keyed by the level's ID, not its name: every
// flattened field addresses levels by name, so a plain rename would otherwise change every
// key at once and credit Classes, Sections and Subjects for a Levels-only edit. The id is
// stable across a rename (`levelIdFor` keeps the persisted one). A level with no id falls
// back to its name — legacy state, where the rename then still marks everything, as before.

/** `${curriculum}|${levelName}` -> rename-stable key for that level. */
const levelKeys = (md: Loose): Record<string, string> => {
  const out: Record<string, string> = {}
  Object.entries((md.levelsByCurriculum ?? {}) as Loose).forEach(([curriculum, cfg]) => {
    ;((cfg?.levels ?? []) as any[]).forEach((level) => {
      const name = String(level?.name ?? '')
      const id = String(level?.id ?? '').trim()
      out[`${curriculum}|${name}`] = `${curriculum}|${id || name}`
    })
  })
  return out
}

const levelKeyFor = (keys: Record<string, string>, curriculum: string, level: string): string =>
  keys[`${curriculum}|${level}`] ?? `${curriculum}|${level}`

const levelNames = (md: Loose): Loose => {
  const out: Loose = {}
  Object.entries((md.levelsByCurriculum ?? {}) as Loose).forEach(([curriculum, cfg]) => {
    out[curriculum] = ((cfg?.levels ?? []) as any[]).map((level) => level?.name ?? '')
  })
  return out
}

const classNames = (md: Loose): Loose => {
  const keys = levelKeys(md)
  const out: Loose = {}
  Object.entries((md.levelsByCurriculum ?? {}) as Loose).forEach(([curriculum, cfg]) => {
    Object.entries((cfg?.levelDetails ?? {}) as Loose).forEach(([level, details]) => {
      out[levelKeyFor(keys, curriculum, level)] = ((details?.classes ?? []) as any[]).map(
        (cls) => cls?.name ?? ''
      )
    })
  })
  return out
}

/** Sections per (curriculum, level, grade), unioned across branches so a branch change is silent. */
const sectionsByClass = (md: Loose): Loose => {
  const keys = levelKeys(md)
  const out: Record<string, Set<string>> = {}
  Object.values((md.classesReviewRowsByBranch ?? {}) as Loose).forEach((rows) => {
    ;((rows ?? []) as any[]).forEach((row) => {
      const curriculum = String(row?.curriculum ?? '')
      const level = String(row?.level ?? '')
      const key = `${levelKeyFor(keys, curriculum, level)}|${row?.gradeName ?? ''}`
      const set = (out[key] ??= new Set<string>())
      ;((row?.sections ?? []) as unknown[]).forEach((section) => set.add(String(section)))
    })
  })
  const sorted: Loose = {}
  Object.entries(out).forEach(([key, set]) => {
    sorted[key] = Array.from(set).sort()
  })
  return sorted
}

/**
 * Subjects per level, read through the level list so the entry is keyed by level id. A
 * selection whose level no longer exists is dropped: that is a Levels edit, not a Subjects one.
 */
const subjectsByLevel = (md: Loose): Loose => {
  const keys = levelKeys(md)
  const selections = (md.subjectSelectionsByLevel ?? {}) as Loose
  const out: Loose = {}
  Object.entries((md.levelsByCurriculum ?? {}) as Loose).forEach(([curriculum, cfg]) => {
    ;((cfg?.levels ?? []) as any[]).forEach((level) => {
      const name = String(level?.name ?? '')
      const subjects = selections[subjectLevelKey(curriculum, name)]
      if (subjects === undefined) return
      out[levelKeyFor(keys, curriculum, name)] = [...((subjects ?? []) as unknown[])]
        .map(String)
        .sort()
    })
  })
  return out
}

const projectionChanged = (
  before: Loose,
  after: Loose,
  sourceField: string,
  project: (md: Loose) => Loose
): boolean => sourceField in after && !same(project(before), project(after))

/**
 * The analytics positions a user write to wizard state edits, given the state before it, the
 * partial write, and the wizard `currentStep` the user is on. Returns [] for a no-op write.
 *
 * `curriculum_review` is never returned: on the collapsed Academic Structure page the review
 * table is derived from the structure and has no editing surface of its own (rule 4).
 *
 * `subStep` is the Teacher Allocation screen the write came from (`teachers` or
 * `review-teachers`). Both screens edit the same `teachers` field, so it is the only way to
 * tell an Add Teachers edit from a Review Teachers one; without it a teachers change counts
 * as Add Teachers.
 */
export const editedPositionsIn = (
  prev: Loose,
  next: Loose,
  currentStep: number,
  subStep?: string | null
): string[] => {
  const before: Loose = prev.manualData ?? {}
  const after: Loose = next.manualData ?? {}
  const hits: string[] = []
  const mark = (position: string, edited: boolean) => {
    if (edited && stepCompletingPosition(position) === currentStep) hits.push(position)
  }

  // Setup Basics. `mode` (the mode tile + the country picker) is NOT diffed per write: both
  // are persisted to preferences the moment they change, so a reload or resume before Next
  // brings the pick back with this in-memory flag gone. It is judged by value on Next
  // instead, see modeSelectionEdited.
  mark('curriculum_curriculum', changed(before, after, 'curriculums'))

  // Institution Setup: the same field ownership the touched-field tracking uses.
  Object.entries(SETUP_FIELDS_BY_POSITION).forEach(([position, fields]) => {
    mark(
      position,
      fields.some((field) => changed(before, after, field))
    )
  })

  // Academic Structure.
  mark('curriculum_levels', projectionChanged(before, after, 'levelsByCurriculum', levelNames))
  mark('curriculum_classes', projectionChanged(before, after, 'levelsByCurriculum', classNames))
  mark(
    'curriculum_sections',
    projectionChanged(before, after, 'classesReviewRowsByBranch', sectionsByClass)
  )
  mark(
    'curriculum_subjects',
    projectionChanged(before, after, 'subjectSelectionsByLevel', subjectsByLevel)
  )

  // Teacher Allocation tabs. Add Teachers and Review Teachers are two screens of one tab
  // writing one field, so the write's own screen decides which position it edited.
  const teachersChanged = changed(before, after, 'teachers')
  mark('assign_teachers', teachersChanged && subStep !== 'review-teachers')
  mark('assign_review_teachers', teachersChanged && subStep === 'review-teachers')
  mark('assign_students', changed(before, after, 'students'))
  mark('assign_lesson_planning', changed(before, after, 'lessonPlanningData'))

  return hits
}

/**
 * Whether the Setup Basics `mode` position (the mode tile and the country picker share it) was
 * edited: the value the user is completing the page with differs from what the wizard pre-fills.
 *
 * Judged by value when Next is clicked, not per write like the rest of editedPositionsIn. Mode
 * and country are saved to preferences as soon as they change, so after a reload or resume the
 * user's pick comes back as plain state and no write ever shows the change. Comparing against
 * the default instead also means that switching away and back again is not an edit.
 *
 * `defaultCountry` is the country Step1Mode pre-fills (tenant's stored country, else
 * detected); `undefined` while that is unresolved, in which case only the mode is compared
 * (rule 4). A resolved `null` default means nothing was pre-filled, so any country is an edit.
 */
export const modeSelectionEdited = (
  current: {mode: string | null; country: string | null},
  defaultCountry: string | null | undefined
): boolean => {
  if (current.mode != null && current.mode !== DEFAULT_WIZARD_MODE) return true
  if (defaultCountry === undefined) return false
  return (current.country ?? null) !== defaultCountry
}
